import { cn } from '@reflex/ui/lib/utils';
import type { NavDrillGroup, NavItem } from './nav-types';

/**
 * Canonical row styling for expanded-sidebar nav entries. Every nav style
 * (drill, expand, flat, flyout) renders rows through this helper so the
 * active treatment (gradient + left accent border) stays identical across
 * styles and only ever changes in one place.
 */
export function navRowClasses(active: boolean, className?: string): string {
  return cn(
    'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-all duration-150 border-l-2',
    active
      ? 'bg-gradient-to-r from-selected to-selected/70 text-selected-foreground font-medium border-selected-border'
      : 'text-sidebar-foreground hover:bg-sidebar-accent/30 border-transparent',
    className,
  );
}

/**
 * True when `pathname` is the group's overview page or any route belonging
 * to the group (its base path subtree or one of its item subtrees).
 */
export function groupContainsPath(group: NavDrillGroup, pathname: string): boolean {
  if (pathname === group.basePath || pathname.startsWith(group.basePath + '/')) return true;
  return group.items.some((item) => pathname === item.to || pathname.startsWith(item.to + '/'));
}

export function getActiveDrillGroup(
  pathname: string,
  groups: NavDrillGroup[],
): NavDrillGroup | null {
  let best: NavDrillGroup | null = null;
  for (const group of groups) {
    const basePathMatch = pathname === group.basePath || pathname.startsWith(group.basePath + '/');
    const itemMatch =
      !basePathMatch &&
      group.items.some((item) => pathname === item.to || pathname.startsWith(item.to + '/'));
    if (basePathMatch || itemMatch) {
      if (!best || group.basePath.length > best.basePath.length) best = group;
    }
  }
  return best;
}

/**
 * Returns the `to` of the most-specific item in `items` whose route prefix
 * matches `pathname`. Ensures that when item paths nest (e.g. `/town` and
 * `/town/flows`), only the deepest match is highlighted as active.
 */
export function getActiveDrillItemPath(
  items: readonly Pick<NavItem, 'to'>[],
  pathname: string,
): string | null {
  let best: string | null = null;
  for (const { to } of items) {
    if (pathname === to || pathname.startsWith(to + '/')) {
      if (best === null || to.length > best.length) best = to;
    }
  }
  return best;
}

/** Label of the deepest matching nav item, or `fallback` when none match. */
export function getActiveNavItemLabel(
  items: readonly Pick<NavItem, 'to' | 'label'>[],
  pathname: string,
  fallback: string,
): string {
  const to = getActiveDrillItemPath(items, pathname);
  return items.find((item) => item.to === to)?.label ?? fallback;
}

export const drillInVariants = {
  initial: { x: 40, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: 40, opacity: 0 },
};

export const drillOutVariants = {
  initial: { x: -40, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -40, opacity: 0 },
};

export const slideTransition = {
  type: 'tween',
  duration: 0.15,
  ease: [0.25, 0.1, 0.25, 1],
} as const;
